import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ordersAPI } from '../../api/orders';
import { resolveUploadUrl } from '../../utils/imageUrl';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_BYTES = 10 * 1024 * 1024;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Title = styled.div`
  font-weight: 600;
  color: var(--text-color, #333);
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #666;
`;

const PreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Preview = styled.img`
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #ddd;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--primary-color, #8b4513);
  background: #fff;
  color: var(--primary-color, #8b4513);
  border-radius: 6px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const ErrorText = styled.p`
  margin: 0;
  color: #c0392b;
  font-size: 0.875rem;
`;

interface CakePhotoUploadProps {
  value: string | null;
  onChange: (url: string | null) => void;
  onUploadingChange?: (uploading: boolean) => void;
}

const CakePhotoUpload: React.FC<CakePhotoUploadProps> = ({ value, onChange, onUploadingChange }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      onUploadingChangeRef.current?.(false);
    };
  }, []);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so choosing the same file again re-triggers onChange.
    e.target.value = '';
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(t('productDetail.cakePhotoErrorType'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(t('productDetail.cakePhotoErrorSize'));
      return;
    }

    setError(null);
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const { url } = await ordersAPI.uploadCustomCakeImage(file);
      if (!mountedRef.current) return;
      onChange(url);
    } catch (err: any) {
      if (!mountedRef.current) return;
      const status = err?.response?.status;
      if (status === 413) setError(t('productDetail.cakePhotoErrorSize'));
      else if (status === 429) setError(t('productDetail.cakePhotoErrorRateLimit'));
      else if (status === 400) setError(t('productDetail.cakePhotoErrorType'));
      else setError(t('productDetail.cakePhotoErrorGeneric'));
    } finally {
      if (mountedRef.current) {
        setUploading(false);
        onUploadingChange?.(false);
      }
    }
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <Wrapper data-testid="cake-photo-upload">
      <Title>{t('productDetail.cakePhotoTitle')}</Title>
      <Hint>{t('productDetail.cakePhotoHint')}</Hint>
      <HiddenInput
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFile}
        data-testid="cake-photo-input"
        aria-label={t('productDetail.cakePhotoChoose')}
      />
      {value ? (
        <PreviewRow>
          <Preview src={resolveUploadUrl(value)} alt={t('productDetail.cakePhotoPreviewAlt')} data-testid="cake-photo-preview" />
          <Actions>
            <ActionButton type="button" onClick={openPicker} disabled={uploading} data-testid="cake-photo-replace">
              {t('productDetail.cakePhotoReplace')}
            </ActionButton>
            <ActionButton type="button" onClick={() => { setError(null); onChange(null); }} disabled={uploading} data-testid="cake-photo-remove">
              {t('productDetail.cakePhotoRemove')}
            </ActionButton>
          </Actions>
        </PreviewRow>
      ) : (
        <Actions>
          <ActionButton type="button" onClick={openPicker} disabled={uploading} data-testid="cake-photo-choose">
            {uploading ? t('productDetail.cakePhotoUploading') : t('productDetail.cakePhotoChoose')}
          </ActionButton>
        </Actions>
      )}
      {value && uploading && <Hint role="status">{t('productDetail.cakePhotoUploading')}</Hint>}
      {error && <ErrorText role="alert" data-testid="cake-photo-error">{error}</ErrorText>}
    </Wrapper>
  );
};

export default CakePhotoUpload;
